import { DefaultContext, useQuery } from '@apollo/client'
import { useSelector } from '@xstate/react'
import React, { FC, useCallback, useContext, useEffect } from 'react'
import styled from 'styled-components'

import { SanityAccountDashboardPageFragment } from '../../../../../graphql/gatsby'
import { RewardsDocument } from '../../../../../graphql/search'
import useLogAndCaptureError from '../../../../../hooks/useLogAndCaptureError'
import { ApolloClientsContext } from '../../../../../lib/graphql/apollo'
import { RewardsOnboardingServiceContext } from '../../../../../lib/rewards/onboarding/context'
import { openModal } from '../../../../../lib/rewards/onboarding/modal'
import { RewardsOnboardingStateObject } from '../../../../../lib/rewards/onboarding/state/machine-rewards-onboarding'
import {
  isRewardsSnapshotPositiveSales,
  parseRewardsSnapshots,
} from '../../../../../lib/rewards/snapshots'
import Spinner from '../../../../Spinner'
import { DashboardPageSections } from '../../dashboard/DashboardPageSections'
import QueryFallback from '../../QueryFallback'
import { RewardsOnboardingModal } from '../onboarding/RewardsOnboardingModal'
import { DfhRewardsCard } from './DfhRewardsCard'
import { NoSalesContainer } from './NoSalesContainer'
import { SalesHistoryCard } from './SalesHistoryCard'
import { SalesOverviewCard } from './SalesOverviewCard'
import { RewardsTierModal } from './SalesOverviewCard/RewardsTierModal'

export interface RewardsDashboardProps {
  sanityAccountDashboardPage: SanityAccountDashboardPageFragment['sanityAccountDashboardPage']
}

const selectOnboardingPending = (state: RewardsOnboardingStateObject) => state.matches('pending')

export const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 1024px) {
    gap: 32px;
  }
`

export const RewardsDashboard: FC<RewardsDashboardProps> = ({ sanityAccountDashboardPage }) => {
  const { comma: commaClient } = useContext(ApolloClientsContext)
  const rewardsOnboardingService = useContext(RewardsOnboardingServiceContext)
  const isPending = useSelector(rewardsOnboardingService, selectOnboardingPending)
  const { data, error, loading, refetch } = useQuery(RewardsDocument, {
    client: commaClient,
    context: { useLatestTokenIfLoggedIn: true } as DefaultContext,
  })
  useLogAndCaptureError(error)

  const onContinue = useCallback(() => {
    rewardsOnboardingService.send('CONTINUE')
  }, [rewardsOnboardingService])

  useEffect(() => {
    if (loading || error || !data) return
    setTimeout(() => {
      openModal()
    }, 2000)
  }, [data, error, loading])

  if (loading) {
    return <Spinner loading />
  }
  if (error) {
    return (
      <QueryFallback buttonText="Retry Loading Rewards" error={error} onRetry={() => refetch()} />
    )
  }
  if (!data) {
    return null
  }
  const availableBalance = data.customer?.availableBalance
  const rewardsSnapshots = data.customer?.rewardsSnapshots?.nodes ?? []
  const [currentSnapshot, previousSnapshot] = parseRewardsSnapshots(rewardsSnapshots)
  const hasPositiveSales = [currentSnapshot, previousSnapshot].every(isRewardsSnapshotPositiveSales)
  const shouldDisplaySalesAnalytics =
    (currentSnapshot?.orderCount || 0) > 0 || Number(currentSnapshot?.sales.total.amount || 0) > 0

  return (
    <DashboardContainer>
      {isPending && <RewardsOnboardingModal onContinue={onContinue} />}
      {!hasPositiveSales && <NoSalesContainer />}
      <SalesOverviewCard currentSnapshot={currentSnapshot} />
      <DfhRewardsCard availableBalance={availableBalance} previousSnapshot={previousSnapshot} />
      {shouldDisplaySalesAnalytics && <SalesHistoryCard rewardsSnapshots={rewardsSnapshots} />}
      <DashboardPageSections sanityAccountDashboardPage={sanityAccountDashboardPage} />
      <RewardsTierModal currentSnapshot={currentSnapshot} />
    </DashboardContainer>
  )
}
